/**
 * Minimal vector math on plain objects.
 *
 * Deliberately NOT THREE.Vector3. Everything in src/systems/ and src/ai/ must
 * run headless in Node for the deterministic sim tests, so the simulation layer
 * stays free of any Three.js import. Only src/render/, src/engine/ and the
 * *view* half of actors may touch Three.
 */

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export const vec3 = (x = 0, y = 0, z = 0): Vec3 => ({ x, y, z });

export const clone = (v: Vec3): Vec3 => ({ x: v.x, y: v.y, z: v.z });

export const set = (out: Vec3, x: number, y: number, z: number): Vec3 => {
  out.x = x;
  out.y = y;
  out.z = z;
  return out;
};

export const copy = (out: Vec3, v: Vec3): Vec3 => set(out, v.x, v.y, v.z);

export const add = (a: Vec3, b: Vec3): Vec3 => vec3(a.x + b.x, a.y + b.y, a.z + b.z);

export const sub = (a: Vec3, b: Vec3): Vec3 => vec3(a.x - b.x, a.y - b.y, a.z - b.z);

export const scale = (v: Vec3, s: number): Vec3 => vec3(v.x * s, v.y * s, v.z * s);

export const dot = (a: Vec3, b: Vec3): number => a.x * b.x + a.y * b.y + a.z * b.z;

export const lengthOf = (v: Vec3): number => Math.sqrt(dot(v, v));

export const distance = (a: Vec3, b: Vec3): number => lengthOf(sub(a, b));

/** Horizontal (XZ) distance. Used constantly - granny's vision and hearing
 *  ignore height differences, which keeps stairs from behaving strangely. */
export const distanceXZ = (a: Vec3, b: Vec3): number => {
  const dx = a.x - b.x;
  const dz = a.z - b.z;
  return Math.sqrt(dx * dx + dz * dz);
};

export const normalize = (v: Vec3): Vec3 => {
  const len = lengthOf(v);
  return len > 1e-6 ? scale(v, 1 / len) : vec3();
};

export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

export const lerpVec3 = (a: Vec3, b: Vec3, t: number): Vec3 =>
  vec3(lerp(a.x, b.x, t), lerp(a.y, b.y, t), lerp(a.z, b.z, t));

export const clamp = (value: number, min: number, max: number): number =>
  value < min ? min : value > max ? max : value;

export const clamp01 = (value: number): number => clamp(value, 0, 1);

/** Maps `value` from [inMin, inMax] to [outMin, outMax], clamped at both ends. */
export const remap = (
  value: number,
  inMin: number,
  inMax: number,
  outMin: number,
  outMax: number,
): number => {
  if (Math.abs(inMax - inMin) < 1e-9) return outMin;
  return lerp(outMin, outMax, clamp01((value - inMin) / (inMax - inMin)));
};

export const DEG_TO_RAD = Math.PI / 180;
export const RAD_TO_DEG = 180 / Math.PI;

/** Shortest signed angular difference in radians, in (-PI, PI]. */
export const angleDelta = (from: number, to: number): number => {
  let d = (to - from) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d <= -Math.PI) d += Math.PI * 2;
  return d;
};

/** Rotates `current` toward `target` by at most `maxStep` radians. */
export const rotateToward = (current: number, target: number, maxStep: number): number => {
  const delta = angleDelta(current, target);
  if (Math.abs(delta) <= maxStep) return target;
  return current + Math.sign(delta) * maxStep;
};

/** Yaw (radians) of the heading from `from` to `to`, in Three's -Z-forward convention. */
export const yawTowards = (from: Vec3, to: Vec3): number =>
  Math.atan2(to.x - from.x, to.z - from.z);

/**
 * Frame-rate independent exponential smoothing.
 * `smoothing` is the fraction remaining after one second. Use instead of a raw
 * `lerp(a, b, 0.1)` per frame, which is silently frame-rate dependent - the
 * single most common feel bug in a game loop.
 */
export const damp = (current: number, target: number, smoothing: number, dt: number): number =>
  lerp(target, current, Math.pow(clamp01(smoothing), dt));
